<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

const props = withDefaults(
  defineProps<{
    walkersHauteRoute?: boolean;
    tourOfTheJungfrau?: boolean;
    /** When true, show only Walker's Haute Route and Tour of the Jungfrau (respecting their props). */
    onlySwissTreks?: boolean;
  }>(),
  { walkersHauteRoute: true, tourOfTheJungfrau: true, onlySwissTreks: false },
);

const page = usePage().value;
const nuxtApp = useNuxtApp();
const isMobile = ref(false);
const windowWidth = ref(0); // default value
// Filter treks by location
interface Trek {
  isListed: boolean;
  trekLocation: string;
  uri?: string;
}

function updateWidth() {
  isMobile.value = window.innerWidth <= 991;
  windowWidth.value = window.innerWidth;
}
onMounted(() => {
  isMobile.value = window.innerWidth <= 991;
  // Access the window object only after the component is mounted
  windowWidth.value = window.innerWidth;
  updateWidth();
  window.addEventListener("resize", updateWidth);
});

onUnmounted(async () => {
  window.removeEventListener("resize", updateWidth);

  // Clean up any ScrollTrigger instances for this component
  if (process.client) {
    try {
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");
      ScrollTrigger.getAll().forEach((trigger) => {
        if (
          trigger.vars.trigger &&
          trekRefs.value.includes(trigger.vars.trigger)
        ) {
          trigger.kill();
        }
      });
    } catch (error) {
      console.warn("Failed to cleanup ScrollTrigger instances:", error);
    }
  }
});

const WALKERS_HAUTE_ROUTE_URI = "walkers-haute-route";
const TOUR_OF_THE_JUNGFRAU_URI = "tour-of-the-jungfrau";

const treks = computed(() => {
  const europeTreks =
    page?.treks?.filter(
      (trek: Trek) => trek?.isListed && trek?.trekLocation === "Europe",
    ) ?? [];
  if (props.onlySwissTreks) {
    return europeTreks.filter((trek: Trek) => {
      const uri = (trek?.uri ?? "").replace(/^\/+/, "");
      if (uri === WALKERS_HAUTE_ROUTE_URI) return props.walkersHauteRoute;
      if (uri === TOUR_OF_THE_JUNGFRAU_URI) return props.tourOfTheJungfrau;
      return false;
    });
  }
  return europeTreks.filter((trek: Trek) => {
    const uri = (trek?.uri ?? "").replace(/^\/+/, "");
    if (uri === WALKERS_HAUTE_ROUTE_URI) return props.walkersHauteRoute;
    if (uri === TOUR_OF_THE_JUNGFRAU_URI) return props.tourOfTheJungfrau;
    return true;
  });
});
const trekRefs = ref<HTMLElement[]>([]);
function addRef(el: HTMLElement | null, index: number) {
  if (el instanceof HTMLElement) {
    trekRefs.value[index] = el;
  }
}
async function runNavAnimation() {
  if (!process.client) return;

  try {
    const { gsap } = await import("gsap");

    //   console.log("Mounted, total treks:", trekRefs.value.length);
    trekRefs.value.forEach((el, index) => {
      if (!el) {
        console.error("Element not found at index:", index);
        return;
      }

      // Check if element is still in DOM
      if (!document.contains(el)) {
        console.warn("Element no longer in DOM, skipping animation");
        return;
      }

      // console.log("Animating element:", el);
      gsap.fromTo(
        el,
        { opacity: 0, y: 50 }, // Only animate opacity and y position, not scale
        {
          opacity: 1,
          y: 0,
          duration: 1.5, // Shorter duration
          scrollTrigger: {
            trigger: el,
            start: "top 90%", // Adjusted for a later start to smooth into view
            end: "bottom 90%", // Sync end point with start for consistency
            toggleActions: "play none none reverse",
            markers: false,
            scrub: 0.5, // Reduced scrub delay
          },
          ease: "power2.out", // Simpler easing
        },
      );
    });
  } catch (error) {
    console.warn("Navigation animation failed:", error);
  }
}
// On Suspense resolved event
nuxtApp.hook("page:finish", () => {
  setTimeout(() => {
    runNavAnimation();
  }, 750);
});
</script>

<template>
  <section class="treks-nav-wrap">
    <div
      v-for="(trek, index) in treks"
      :key="index"
      :ref="(el) => addRef(el, index)"
      class="trek"
    >
      <NuxtLink class="btn from-left" :to="`/${trek?.uri}`">
        <div v-if="trek?.trekDistance" class="trek-stats">
          <p v-if="trek?.trekDistance" class="trek-distance">
            {{ trek?.trekDistance }}
          </p>
          <p v-if="trek?.trekElevation" class="trek-elevation">
            {{ trek?.trekElevation }}
          </p>
        </div>
        <div class="trek-image">
          <NuxtPicture
            v-if="trek?.coverImage && !isMobile"
            format="webp"
            loading="lazy"
            :src="trek?.coverImage.url"
            :width="600"
            :height="500"
            :alt="trek?.coverImage.alt || trek?.title || 'Trek cover image'"
            sizes="sm:100vw md:50vw lg:600px"
          />
          <NuxtPicture
            v-if="trek?.coverImage && isMobile"
            format="webp"
            loading="lazy"
            :src="trek?.coverImage.urlMobile"
            :width="400"
            :height="300"
            :alt="trek?.coverImage.alt || trek?.title || 'Trek cover image'"
            sizes="sm:100vw md:50vw lg:400px"
          />
        </div>
        <div class="trek-title">
          <h3>{{ trek?.title }}</h3>
          <div v-if="trek?.trekGrade === 'Grade 1'" class="trek-grade">
            <AppSVGGradeEasy />
            <p>Easy</p>
          </div>
          <div v-if="trek?.trekGrade === 'Grade 2'" class="trek-grade">
            <AppSVGGradeModerate />
            <p>Moderate</p>
          </div>
          <div v-if="trek?.trekGrade === 'Grade 3'" class="trek-grade">
            <AppSVGGradeDifficult />
            <p>Difficult</p>
          </div>
          <div v-if="trek?.trekGrade === 'Grade 4'" class="trek-grade">
            <AppSVGGradeToughest />
            <p>Toughest</p>
          </div>
        </div>
      </NuxtLink>
    </div>
  </section>
</template>
